import classNames from "classnames";
import React, { DragEventHandler, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useCourtFreeDraw } from "../../hooks";
import { usePlayContext } from "../../pages/Play/play-context";
import { useGetPlayingSurfaceByIdQuery, useGetUserPlaySettingsQuery } from "../../redux/slices";
import { FrameStencil, SportPlay } from "../../types";
import { DragAndDropPreview } from "../drag-and-drop-preview/drag-and-drop-preview";
import { FastPlaceGhost } from "../fast-place-ghost/fast-place-ghost";
import { FrameStencilDrawing } from "../frame-stencil-drawing/frame-stencil-drawing";
import { FreeDrawPreview } from "../free-draw-preview/free-draw-preview";
import { useSelectActionMenu } from "../select-action-menu";
import { Grid, PlayingSurfaceContextMenu, PlayingSurfaceLines } from "./components";
import { useOnDrop, usePlayingSurfaceContextApi } from "./hooks";
import { PlayingSurfaceContext } from "./playing-surface-context";
import styles from "./playing-surface.module.scss";
import { getSurfaceUnits } from "./utils";

const onDragOverDisable: DragEventHandler<HTMLDivElement> = (event) => {
  event.preventDefault();
};

export interface OutOfBounds {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

export interface PlayingSurfaceProps<TPlay> {
  play: TPlay;
  stencils: FrameStencil[];
  outOfBounds?: OutOfBounds;
  isEditable?: boolean;
  fitContainer?: boolean;
}

export const PlayingSurface = <TPlay extends SportPlay>({
  play,
  stencils,
  outOfBounds,
  isEditable = false,
  fitContainer = true,
}: PlayingSurfaceProps<TPlay>) => {
  const { sport, playSurfaceId, playSurfaceView, courtOptions } = play;

  /** ======== Element Refs ======== */
  const surfaceContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  /** ======== Play Settings ======== */
  const location = useLocation();
  const readOnly = location.pathname.includes("readonly");

  const { data: userPlaySettings } = useGetUserPlaySettingsQuery();
  const showBackground = userPlaySettings?.viewCourtBgr || false;
  const showGrid = (userPlaySettings?.viewGrid && !readOnly) || false;

  /** ======== Playing Surface ======== */
  const { data: playSurface } = useGetPlayingSurfaceByIdQuery({ id: playSurfaceId });

  const playSurfaceData = playSurface?.drawData;
  const playSurfaceInfo = playSurfaceData?.info;

  const view = playSurfaceInfo?.courtViews?.[playSurfaceView];
  const viewBox = view?.viewBox || "0 0 100 100";
  const viewBoxValues = viewBox.split(" ");
  const viewBoxX = parseFloat(viewBoxValues[0] || "");
  const viewBoxY = parseFloat(viewBoxValues[1] || "");
  const viewBoxWidth = parseFloat(viewBoxValues[2] || "");
  const viewBoxHeight = parseFloat(viewBoxValues[3] || "");

  const adjustedViewBoxX = viewBoxX - (outOfBounds?.left || 0);
  const adjustedViewBoxY = viewBoxY - (outOfBounds?.top || 0);
  const adjustedViewBoxWidth = viewBoxWidth + (outOfBounds?.left || 0) + (outOfBounds?.right || 0);
  const adjustedViewBoxHeight = viewBoxHeight + (outOfBounds?.top || 0) + (outOfBounds?.bottom || 0);

  const { surfaceWidthUnits, surfaceHeightUnits } = getSurfaceUnits(outOfBounds, view);

  const adjustedViewBox = `${adjustedViewBoxX} ${adjustedViewBoxY} ${adjustedViewBoxWidth} ${adjustedViewBoxHeight}`;

  /** ======== Context API ======== */
  const { animationTimerApi, drawToolbarApi } = usePlayContext();

  const { effectiveTime, time } = animationTimerApi;
  const { freeDraw } = drawToolbarApi;

  const isTime0 = effectiveTime === 0 || time === 0;

  /** ======== Stencil Change Tooltip ======== */
  const { openSelectActionMenu, closeSelectActionMenu } = useSelectActionMenu();

  /** ======== Drop ======== */
  const { onDrop, previewAction } = useOnDrop({
    surfaceContainerRef,
    svgRef,
    adjustXUnits: adjustedViewBoxX,
    adjustYUnits: adjustedViewBoxY,
    surfaceWidthUnits,
    surfaceHeightUnits,
    openTooltip: openSelectActionMenu,
  });

  /** ======== Free Draw ======== */
  const surfaceFreeDraw = useCourtFreeDraw({
    freeDraw: isEditable && isTime0 && freeDraw.isActive,
    frameStencils: stencils,
    openTooltip: openSelectActionMenu,
    closeTooltip: closeSelectActionMenu,
  });

  const { smoothFreeDrawPoints, freeDrawPoints, onFreeDraw, onFreeDrawStart, onFreeDrawEnd } = surfaceFreeDraw;

  const playingSurfaceContextApi = usePlayingSurfaceContextApi({ isEditable });
  const { editPlayerLabelApi } = playingSurfaceContextApi;
  const { playerInputRef, isEditing, playerEditing, handleInputChange, handleInputBlur, handleKeyDown, position } =
    editPlayerLabelApi;

  const svgStyle = (() => {
    if (play.orientation === 90 || play.orientation === 270) {
      if (adjustedViewBoxWidth > adjustedViewBoxHeight)
        return { maxHeight: (adjustedViewBoxHeight / adjustedViewBoxWidth) * 100 + "%" };
      else return { maxWidth: (adjustedViewBoxWidth / adjustedViewBoxHeight) * 100 + "%" };
    }
  })();

  return (
    <div className={classNames("", [fitContainer && styles.fitContainer])}>
      <PlayingSurfaceContext.Provider value={playingSurfaceContextApi}>
        <PlayingSurfaceContextMenu isEditable={isEditable} stencils={stencils}>
          <div
            ref={surfaceContainerRef}
            className={styles.playingSurface}
            onDrop={isEditable && isTime0 ? onDrop : undefined}
            onDragOver={onDragOverDisable}
          >
            <svg
              ref={svgRef}
              className={classNames(styles.playingSurfaceSvg, {
                [styles.freeDrawEnabled || ""]: isEditable && isTime0 && freeDraw.isActive,
              })}
              {...(svgStyle ? { style: svgStyle } : {})}
              viewBox={adjustedViewBox}
              transform={`rotate(${play.orientation})`}
              onMouseMove={onFreeDraw}
              onMouseDown={onFreeDrawStart}
              onMouseUp={onFreeDrawEnd}
            >
              {/* Background */}
              {showBackground && (
                <image
                  href={playSurfaceInfo?.backgroundImage}
                  x={adjustedViewBoxX}
                  y={adjustedViewBoxY}
                  width={adjustedViewBoxWidth}
                  height={adjustedViewBoxHeight}
                  preserveAspectRatio="xMinYMin slice"
                />
              )}

              {showGrid && (
                <Grid
                  surfaceHeightUnits={surfaceHeightUnits}
                  adjustedViewBoxX={adjustedViewBoxX}
                  adjustedViewBoxY={adjustedViewBoxY}
                  adjustedViewBoxWidth={adjustedViewBoxWidth}
                  adjustedViewBoxHeight={adjustedViewBoxHeight}
                />
              )}

              {playSurfaceData && (
                <PlayingSurfaceLines
                  playingSurfaceData={playSurfaceData}
                  courtOptions={courtOptions}
                  sport={sport}
                  showBackground={showBackground}
                />
              )}

              {/* Stencils */}
              <FrameStencilDrawing frameStencils={stencils} orientation={play.orientation} isEditable={isEditable} />

              {smoothFreeDrawPoints.length > 0 && freeDrawPoints.length > 0 && freeDraw.isActive && (
                <FreeDrawPreview smoothFreeDrawPoints={smoothFreeDrawPoints} freeDrawPoints={freeDrawPoints} />
              )}

              {previewAction && <DragAndDropPreview actionData={previewAction} />}

              {isEditable && isTime0 && (
                <FastPlaceGhost
                  frameStencils={stencils}
                  courtRef={surfaceContainerRef}
                  svgRef={svgRef}
                  courtWidthUnits={surfaceWidthUnits}
                  courtHeightUnits={surfaceHeightUnits}
                  adjustXUnits={adjustedViewBoxX}
                  adjustYUnits={adjustedViewBoxY}
                />
              )}
            </svg>

            {/* Edit Player Label */}
            <input
              ref={playerInputRef}
              className={classNames(styles.input, { [styles.show || ""]: isEditable && isEditing })}
              type="text"
              style={{ left: position?.x, top: position?.y }}
              value={playerEditing?.label || ""}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
            />
          </div>
        </PlayingSurfaceContextMenu>
      </PlayingSurfaceContext.Provider>
    </div>
  );
};
